import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

/**
 * Minimal read model of an ODP content feed document.
 *
 * Only the fields the chat widget needs are modelled here so the app bundle
 * does not depend on the standalone `odp-content` service package.
 */
export type OdpContentDocument = {
  id: string;
  type: string;
  resourceRef: string;
  title: string;
  text: string;
  location: string;
  metadata?: Record<string, unknown>;
};

/** Feeds the chat widget reads to describe the page the user is on. */
export type OdpContentDocumentType = 'techdocs' | 'academy' | 'journeys';

type DocumentsResponse = {
  data: unknown;
};

// The service is deployed standalone, so the browser reaches it through the auth-gateway proxy.
const AUTH_GATEWAY_ROUTE = 'odp-content';
const REQUEST_LIMIT = 100;

/** Reads one page of a feed, filtered to a single resource. */
export async function fetchOdpContentDocuments(options: {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
  type: OdpContentDocumentType;
  resourceRef: string;
  /** Techdocs only: narrows the result to the single page at this reader path. */
  pageLocation?: string;
  limit?: number;
  signal?: AbortSignal;
}): Promise<OdpContentDocument[]> {
  const gatewayUrl = await options.discoveryApi.getBaseUrl('auth-gateway');
  const endpoint = `${gatewayUrl.replace(/\/$/, '')}/${AUTH_GATEWAY_ROUTE}/v1/documents`;
  const query = new URLSearchParams({
    type: options.type,
    resourceRef: options.resourceRef,
    limit: String(options.limit ?? REQUEST_LIMIT),
  });
  if (options.pageLocation !== undefined) {
    query.set('pageLocation', options.pageLocation);
  }

  const response = await options.fetchApi.fetch(`${endpoint}?${query}`, {
    signal: options.signal,
  });
  if (!response.ok) {
    throw new Error(
      `ODP content request failed with status ${response.status}`,
    );
  }

  const payload = (await response.json()) as DocumentsResponse;
  return readDocuments(payload.data);
}

function readDocuments(data: unknown): OdpContentDocument[] {
  if (!Array.isArray(data)) {
    return [];
  }
  return data.filter(isOdpContentDocument);
}

function isOdpContentDocument(value: unknown): value is OdpContentDocument {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Partial<OdpContentDocument>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.title === 'string' &&
    typeof candidate.text === 'string' &&
    typeof candidate.location === 'string'
  );
}
